import { z } from 'zod'
import {
  hcpJobKindSchema,
  hcpJobStateSchema,
} from '../enums/hcp-job-kind.enum.js'
import { hcpWorkStatusReadSchema } from '../enums/hcp-work-status.enum.js'
import { jobTypeSchema } from '../enums/job-type.enum.js'
import { QUANTITY_PATTERN } from '../helpers/quantity.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'

/**
 * One line of a Housecall Pro job, as the CRM will send it: HCP's own words
 * for it ("labor" for a service, "materials" for a price-book material).
 */
export const hcpJobLineDtoSchema = z.object({
  kind: z.enum(['labor', 'materials']),
  name: z.string(),
  /** What the technician reads under the name: which tree, which repair. */
  description: z.string(),
  /** Exact decimal string, at most two places: 1.25 hours, 3 nozzles. */
  quantity: z.string().regex(QUANTITY_PATTERN),
  /** Dollars, as an exact decimal string. The same amounts `parseMoney` accepts. */
  unitPrice: moneyAmountSchema,
  taxable: z.boolean(),
  /**
   * The HCP service or material the line is made from, so HCP job-costs it.
   * Null when the proposal does not say which, and the line goes in by name.
   */
  serviceItem: z
    .object({ id: z.string(), type: z.enum(['service', 'material']) })
    .nullable(),
})

export type HcpJobLineDto = z.infer<typeof hcpJobLineDtoSchema>

/**
 * One tree a tree job is for, and the work it was sent with: the kinds of
 * pruning kept and the treatments, or the removal. Written to the tree's
 * history when the job is done (docs/site-visits.md, "Tree work done").
 */
export const hcpJobTreeDtoSchema = z.object({
  /** Null on a proposal frozen before trees kept their id: found by label. */
  treeId: z.string().nullable(),
  label: z.string(),
  jobTypes: z.array(jobTypeSchema),
  treatments: z.array(z.string()),
  removal: z.boolean(),
})

export type HcpJobTreeDto = z.infer<typeof hcpJobTreeDtoSchema>

/** One job an accepted proposal becomes: a site's tree work, or its repairs. */
export const hcpJobDraftDtoSchema = z.object({
  siteName: z.string(),
  kind: hcpJobKindSchema,
  lines: z.array(hcpJobLineDtoSchema),
  /** The lines before tax, each rounded to the cent as HCP rounds a line. */
  amount: moneyAmountSchema,
  /** What the client accepted for this site's kind of work, before tax. */
  acceptedAmount: moneyAmountSchema,
  /**
   * The irrigation problems this job repairs, by their document keys: closed
   * when HCP says the job is done. Empty for a tree job.
   */
  repairKeys: z.array(z.string()),
  /**
   * The trees a tree job is for, and their work. Empty for irrigation.
   * Defaulted, so a page newer than the API it reads still reads it.
   */
  trees: z.array(hcpJobTreeDtoSchema).default([]),
})

export type HcpJobDraftDto = z.infer<typeof hcpJobDraftDtoSchema>

/**
 * A job as the proposal page previews it: the draft, where it would go in
 * HCP, and what stops it or deserves a look first.
 */
export const hcpJobPreviewDtoSchema = hcpJobDraftDtoSchema.extend({
  /** The site the job is for; null when the frozen name matches none, or two. */
  siteId: uuidSchema.nullable(),
  /** The HCP address the site is linked to, where the crew is sent. */
  address: z.object({ id: z.string(), label: z.string() }).nullable(),
  /** What has to be fixed before the job can be created, in words. */
  blockers: z.array(z.string()),
  /**
   * The job as it stands in HCP, once somebody asked for it; null until then.
   * UNCONFIRMED is a job HCP may have made without saying so: a person looks
   * there and settles it before anything is created again.
   */
  record: z
    .object({
      id: uuidSchema,
      state: hcpJobStateSchema,
      hcpJobId: z.string().nullable(),
      invoiceNumber: z.string().nullable(),
      createdAt: z.string().datetime(),
      createdByName: z.string().nullable(),
      /**
       * HCP's work status as last read: one this CRM knows ("scheduled",
       * "complete rated"…), or one it does not, in HCP's words.
       */
      workStatus: hcpWorkStatusReadSchema.nullable(),
      /** When HCP finished it; its repairs were closed then. */
      completedAt: z.string().datetime().nullable(),
    })
    .nullable(),
  /** What a person should look at, though the job can be created. */
  warnings: z.array(z.string()),
})

export type HcpJobPreviewDto = z.infer<typeof hcpJobPreviewDtoSchema>

/** The jobs an accepted version becomes, for its page in the CRM. */
export const proposalHcpJobsDtoSchema = z.object({
  /**
   * Why this version becomes no jobs at all: not accepted, HCP not
   * connected, or its sent document unreadable. Null when it can.
   */
  unavailableReason: z.string().nullable(),
  /** Whether this deployment may create jobs in HCP (see "The key is live"). */
  writesEnabled: z.boolean(),
  /** The client's HCP customer, which every job is created on. */
  customer: z.object({ id: z.string(), name: z.string() }).nullable(),
  jobs: z.array(hcpJobPreviewDtoSchema),
})

export type ProposalHcpJobsDto = z.infer<typeof proposalHcpJobsDtoSchema>
